import type { IntegerLayerScoreInput } from '../types';
import { readLayerScoringToken } from './layer-cell-readers';
import { phpJsonString } from './php-json-values';

const phpTrim = (value: string): string => value.replace(/^[ \t\n\r\0\v]+|[ \t\n\r\0\v]+$/g, '');

export function integerLayerTokenFromCell(value: unknown): string | null {
    return readLayerScoringToken(value);
}

export function integerLayerTokensFromFragments(fragments: readonly unknown[]): string[] {
    const tokens: string[] = [];
    for (const fragment of fragments) {
        const token = integerLayerTokenFromCell(fragment);
        if (token !== null) tokens.push(token);
    }
    return tokens;
}

function isNumeric(value: unknown): boolean {
    if (typeof value === 'number') return Number.isFinite(value);
    if (typeof value !== 'string') return false;
    return /^[ \t\n\r\v\f]*[+-]?(?:\d+\.?\d*|\.\d+)(?:[eE][+-]?\d+)?[ \t\n\r\v\f]*$/.test(value);
}

function phpInt(value: string): number {
    const parsed = Number.parseFloat(phpTrim(value));
    return Number.isFinite(parsed) ? Math.trunc(parsed) : 0;
}

function tinyArithmetic(value: string): string | number {
    for (const operator of ['+', '-', '*', '/'] as const) {
        if (!value.includes(operator)) continue;
        const [left = '', right = ''] = value.split(operator);
        const a = phpInt(left);
        const b = phpInt(right);
        if (operator === '+') return a + b;
        if (operator === '-') return a - b;
        if (operator === '*') return a * b;
        return a / b;
    }
    return value;
}

function resolveDynamicToken(token: string, variables: Readonly<Record<string, number>>): string | number {
    let resolved = token;
    for (const [name, value] of Object.entries(variables)) {
        resolved = resolved.split(`[${name}]`).join(phpJsonString(value));
    }
    return tinyArithmetic(resolved);
}

function canonicalVariableName(name: string): string {
    if (name === 'walking_speed') return 'speed_walking';
    if (name === 'swimming_speed') return 'speed_swimming';
    return name;
}

/** Pure Slice 5a mirror of return_int_score_item_layer_type_name(). */
export function scoreIntegerLayer(input: IntegerLayerScoreInput): number {
    return scoreIntegerLayerWithResolvers(input, token => resolveDynamicToken(token, input.variables),
        name => input.variables[name] ?? 0);
}

/** Shared scoring order for the actual carrier compiler and normalized island.
 * Full carrier resolution must supply its own variable and raw root readers.
 */
export function scoreIntegerLayerWithResolvers(input: IntegerLayerScoreInput,
    resolveVariable: (token: string) => unknown, readExternal: (name: string) => unknown): number {
    const tokens = integerLayerTokensFromFragments(input.fragments);
    if (tokens.length === 0) return Math.floor(input.plugValue);

    for (const token of tokens) {
        const normalized = phpTrim(token).toLowerCase();
        if (normalized === 'inf' || normalized === 'infinity' || /^\{(?:inf|infinity)\}$/i.test(phpTrim(token))) {
            return 99999;
        }
    }

    const overrides = tokens
        .map(token => /^\{(-?\d+)\}$/.exec(token))
        .filter((match): match is RegExpExecArray => match !== null)
        .map(match => Number(match[1]));
    if (overrides.length > 0) return Math.floor(Math.max(...overrides));

    const ordinary = tokens.filter(token => !token.includes('['));
    const bracketed = tokens.filter(token => token.includes('['));
    const ordered = [...ordinary, ...bracketed];
    const additive: number[] = [];
    const subtractive: number[] = [];
    const multiplicative: string[] = [];

    for (const token of ordered) {
        if (token.includes('*') && token.includes('[')) {
            multiplicative.push(token);
            continue;
        }
        if (token.includes('[') && token.includes(']')) {
            const processed = resolveVariable(token);
            if (isNumeric(processed)) additive.push(Number(processed));
            continue;
        }
        if (token.includes('+')) {
            const processed = token.split('+').join('');
            if (isNumeric(processed)) additive.push(Number(processed));
            continue;
        }
        if (token.includes('-') && isNumeric(token)) {
            const processed = token.split('-').join('');
            if (isNumeric(processed)) subtractive.push(Number(processed));
            continue;
        }
        if (isNumeric(token)) additive.push(Number(token));
    }

    let score = additive.reduce((sum, value) => sum + value, 0)
        - subtractive.reduce((sum, value) => sum + value, 0);
    for (const expression of multiplicative) {
        const [multiplierToken = '', variableToken = ''] = expression.split('*');
        const multiplier = resolveVariable(multiplierToken);
        const variableName = canonicalVariableName(variableToken.replace(/^[\[\]]+|[\[\]]+$/g, ''));
        const dynamicValue = variableName === input.layerName ? score : readExternal(variableName);
        if (isNumeric(multiplier) && isNumeric(dynamicValue)) score = Math.floor(Number(multiplier) * Number(dynamicValue));
    }

    if (input.minZero && score < 0) score = 0;
    return Math.floor(score);
}
